import React, { useState } from 'react';

const FormSection = ({ title, children }) => (
  <div className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm mb-6">
    <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
      <span className="w-1.5 h-6 bg-[#4ade80] rounded-full"></span>
      {title}
    </h3>
    {children}
  </div>
);

export default function VolunteerPortal() {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    window.scrollTo(0, 0);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#fcfbf9] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white p-10 rounded-3xl shadow-xl text-center border border-green-50/50">
          <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl">
            ✓
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Application Sent!</h2>
          <p className="text-gray-500 mb-8 leading-relaxed">
            Thank you for wanting to join the Bread & Hope movement. Our volunteer coordinator will review your data and contact you within 48 hours.
          </p>
          <button 
            onClick={() => setSubmitted(false)}
            className="bg-[#4ade80] hover:bg-green-500 text-white font-bold py-3 px-8 rounded-xl transition w-full"
          >
            Back to Portal
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#fcfbf9] min-h-screen py-12 font-sans">
      <div className="container mx-auto px-4 max-w-4xl">
        
        {/* HEADER */}
        <div className="mb-12 text-center">
          <span className="text-green-500 font-bold tracking-widest text-xs uppercase bg-green-50 px-3 py-1 rounded-full">
            Join the Movement
          </span>
          <h1 className="text-4xl font-bold text-gray-900 mt-4 mb-3">Volunteer Portal</h1>
          <p className="text-gray-500 max-w-xl mx-auto">
            Your skills can harvest health and nurture hope. Complete your profile below to start your journey with us.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {/* PERSONAL INFORMATION */}
          <FormSection title="Personal Information">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-tight">Full Name</label>
                <input required type="text" placeholder="e.g. Jane Mwangi" className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-[#4ade80] outline-none transition" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-tight">Email Address</label>
                <input required type="email" placeholder="jane@example.com" className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-[#4ade80] outline-none transition" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-tight">Phone Number (WhatsApp)</label>
                <input required type="tel" placeholder="+254 --- --- ---" className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-[#4ade80] outline-none transition" />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-tight">Location / City</label>
                <input required type="text" placeholder="e.g. Nairobi, Westlands" className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl focus:ring-2 focus:ring-[#4ade80] outline-none transition" />
              </div>
            </div>
          </FormSection>

          {/* INTERESTS & SKILLS */}
          <FormSection title="Skills & Availability">
            <div className="space-y-6">
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase tracking-tight block mb-3">Primary Area of Interest</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {['Food Distribution', 'Event Planning', 'Mentorship'].map((area) => (
                    <label key={area} className="flex items-center p-3 border border-gray-100 rounded-xl bg-gray-50 cursor-pointer hover:bg-green-50 transition">
                      <input type="radio" name="interest" className="text-[#4ade80] focus:ring-[#4ade80]" />
                      <span className="ml-3 text-sm font-medium text-gray-700">{area}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-tight">Availability (Days/Hours)</label>
                <select className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-[#4ade80]">
                  <option>Weekends only</option>
                  <option>Weekdays (Morning)</option>
                  <option>Weekdays (Evening)</option>
                  <option>Flexible / On-call</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-tight">Why do you want to join Bread & Hope?</label>
                <textarea rows="4" placeholder="Tell us a bit about your motivation..." className="w-full p-3 bg-gray-50 border border-gray-100 rounded-xl outline-none focus:ring-2 focus:ring-[#4ade80]"></textarea>
              </div>
            </div>
          </FormSection>

          {/* UPLOADS (Optional but looks professional) */}
          <div className="bg-[#1a2e1a] p-8 rounded-2xl text-white mb-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h4 className="font-bold text-lg mb-1">Background Check (Optional)</h4>
              <p className="text-gray-400 text-xs">A Good Conduct certificate helps us process your application faster.</p>
            </div>
            <label className="bg-white/10 hover:bg-white/20 border border-white/20 px-6 py-3 rounded-xl cursor-pointer transition text-sm font-bold">
              Upload PDF/JPG
              <input type="file" className="hidden" />
            </label>
          </div>

          {/* SUBMIT BUTTON */}
          <div className="flex flex-col gap-4">
            <button 
              type="submit"
              className="w-full bg-[#4ade80] hover:bg-green-500 text-white font-bold py-4 rounded-2xl shadow-xl shadow-green-900/10 transition transform active:scale-[0.98]"
            >
              Submit Application
            </button>
            <p className="text-[10px] text-center text-gray-400">
              By submitting, you agree to our Volunteer Privacy Policy and Code of Conduct.
            </p>
          </div>
        </form>

      </div>
    </div>
  );
}